import type { SignalMessage } from './protocol';

declare const __SIGNALING_URL__: string;

export interface SignalingOptions {
  /** Returns the current session token, or null if not logged in. The function
   *  is called fresh on every (re)connect so a refreshed token is picked up. */
  token: () => string | null;
  /** Tells the worker whether to treat this register as a reconnect to an
   *  existing game (truthy = "I still have game X in memory") or a fresh
   *  session (undefined = "I have no game; clean up any stale ones"). */
  activeGameId: () => string | undefined;
  /** Current app state — used so the server can broadcast presence. */
  appState: () => 'active' | 'background';
  onMessage: (msg: SignalMessage) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

/**
 * Long-lived WebSocket to the Cloudflare Worker. Used for:
 *   - registering this client's userId
 *   - sending/receiving challenges
 *   - WebRTC offer/answer/ICE relay
 *
 * Auto-reconnects with exponential backoff on disconnect.
 */
export class Signaling {
  private ws: WebSocket | null = null;
  private opts: SignalingOptions;
  private reconnectMs = 500;
  private maxReconnectMs = 10_000;
  private destroyed = false;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private static readonly HEARTBEAT_MS = 10_000;
  /** Pong watchdog. Incremented every time we send a ping; reset to 0 whenever
   *  a pong (or any other server message) arrives. If we hit MAX_MISSED without
   *  a reply, the socket is treated as a zombie and force-reconnected — this is
   *  the canonical "readyState lies" symptom on iOS WKWebView. */
  private pendingPings = 0;
  private static readonly MAX_MISSED_PONGS = 2;

  constructor(opts: SignalingOptions) {
    this.opts = opts;
    this.connect();
  }

  isOpen(): boolean {
    return !!this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  send(msg: SignalMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    } else {
      console.warn('[signaling] dropped message — socket not open', msg.t);
    }
  }

  destroy(): void {
    this.destroyed = true;
    if (this.ws) this.ws.close();
  }

  /** Force a reconnect — used after login when the token first becomes
   *  available (or after a logout to drop the authenticated socket). */
  reconnect(): void {
    if (this.ws) {
      try { this.ws.close(); } catch (_) { /* */ }
    }
    this.ws = null;
    this.reconnectMs = 500;
    this.connect();
  }

  private connect(): void {
    if (this.destroyed) return;
    this.ws = new WebSocket(__SIGNALING_URL__);

    this.ws.onopen = () => {
      this.reconnectMs = 500;
      const token = this.opts.token();
      if (!token) {
        // Not logged in. Don't bother trying to register — we'd just get
        // bounced. The signaling channel will sit idle until login completes.
        this.opts.onOpen?.();
        return;
      }
      this.send({
        t: 'register',
        token,
        activeGameId: this.opts.activeGameId(),
        appState: this.opts.appState(),
      });
      this.opts.onOpen?.();
      // Heartbeat — without these, CF Workers can hold a "ready: OPEN" socket
      // that's actually dead, and the server has no way to detect zombies.
      if (this.heartbeatTimer !== null) clearInterval(this.heartbeatTimer);
      this.pendingPings = 0;
      this.heartbeatTimer = setInterval(() => {
        // Force-reconnect if the server has gone silent for >MAX_MISSED pings.
        // Catches the iOS-suspend case where readyState still says OPEN but the
        // underlying socket is dead in both directions.
        if (this.pendingPings >= Signaling.MAX_MISSED_PONGS) {
          console.warn('[signaling] missed pongs — assuming zombie, reconnecting');
          this.reconnect();
          return;
        }
        this.pendingPings += 1;
        this.send({ t: 'ping' });
      }, Signaling.HEARTBEAT_MS);
    };

    this.ws.onmessage = (e) => {
      // Any inbound traffic clears the watchdog — pongs are the heartbeat reply
      // but the server might also push presence / move / stats etc. between
      // pings, and we want all of those to count as "socket alive".
      this.pendingPings = 0;
      try {
        const msg = JSON.parse(e.data) as SignalMessage;
        this.opts.onMessage(msg);
      } catch (err) {
        console.error('[signaling] bad message', err);
      }
    };

    this.ws.onclose = () => {
      if (this.heartbeatTimer !== null) {
        clearInterval(this.heartbeatTimer);
        this.heartbeatTimer = null;
      }
      this.opts.onClose?.();
      if (this.destroyed) return;
      setTimeout(() => this.connect(), this.reconnectMs);
      this.reconnectMs = Math.min(this.reconnectMs * 2, this.maxReconnectMs);
    };

    this.ws.onerror = () => {
      this.ws?.close();
    };
  }
}
